<script setup>
import { LiveChatWidget } from "@livechat/widget-vue";
import { useMenuStore } from "@stores/menu";
import { useSettingsStore } from "@stores/settings";
import { useCookie, useNuxtApp } from "#app";
import { useAuthStore } from "@stores/auth";
import { useCouponStore } from "@stores/coupon";
import { useAlert } from "@stores/alert";
import { useDepositStore } from "@stores/deposit";
import { usePromotionStore } from "@stores/promotion";
import { useSeoStore } from "@stores/seo";
import { useLegitimuzStatusStore } from "@stores/legitimuz-status";
import { useTrackingStore } from "@stores/tracking";
import { useWebsocketStore } from "@stores/websocket";

const nuxtApp = useNuxtApp();

const { showAlert } = useAlert();
const legitimuzStore = useLegitimuzStatusStore();
const route = useRoute();
const menu = useMenuStore();
const settingsStore = useSettingsStore();
const authStore = useAuthStore();
const couponStore = useCouponStore();
const depositStore = useDepositStore();
const promotionStore = usePromotionStore();
const config = useRuntimeConfig();
const seoStore = useSeoStore();
const websocketStore = useWebsocketStore();

const trackingStore = useTrackingStore();
const settings = settingsStore.settings;
const url = useRequestURL();

// Layout
const layoutName = config.public.APP_THEME || "default";

const loading = ref(true);
const isUserOffline = ref(false);

const liveChatConfig = computed(() => {
  let liveChatString = settings.general_live_chat;
  return {
    license: liveChatString.split("/")[0] || liveChatString,
    group: liveChatString.split("/")[1] || false
  };
});

onMounted(() => {

  if (import.meta.client) {
    // Here we initialize tracking system (Today Google Tag Manager)
    trackingStore.actions.initialize();
    websocketStore.initializeWebsocket();

    window.addEventListener("offline", () => {
      console.log("User off");
      isUserOffline.value = true;
    });

    window.addEventListener("online", () => {
      console.log("User on");
      isUserOffline.value = false;

      // After the user back online, we will reload the page
      reloadNuxtApp({
        force: true
      });
    });
  }
});

nuxtApp.hook("app:created", () => {
  loading.value = true;
});

// TODO: Refactor this to a plugin or something like that
nuxtApp.hook("app:mounted", async () => {
  loading.value = false;

  if (import.meta.client && authStore.isLoggedIn) {
    // This will check once at initial load and set the cache
    await legitimuzStore.checkStatus(true);
  }

  if (import.meta.client) {
    const router = useRoute();
    const btag = router.query?.btag;
    const src = router.query?.src;
    const utmCampaign = router.query?.utm_campaign;
    const utmContent = router.query?.utm_content;
    const utmSource = router.query?.utm_source;
    const refererId = router.query?.ref;
    const code = window.location.search.match(/[?&]code=([^&]*)/)?.[1];
    const login = router.query?.login;
    const clickid = router.query?.clickid;
    const utmMedium = router.query?.utm_medium;
    const utmTerm = router.query?.utm_term;
    const placement = router.query?.placement;
    const afp = router.query?.afp;
    const afp1 = router.query?.afp1;
    const afp2 = router.query?.afp2;
    const afp3 = router.query?.afp3;
    const afp4 = router.query?.afp4;
    const afp5 = router.query?.afp5;
    const afp6 = router.query?.afp6;
    const afp7 = router.query?.afp7;
    const afp8 = router.query?.afp8;
    const afp9 = router.query?.afp9;
    const fbcParam = router.query?.fbc;
    const fbpParam = router.query?.fbp;
    const shareCode = router.query?.shareCode;
    const source_id = router.query?.source_id;
    const event_id = router.query?.event_id;
    const campaign_id = router.query?.campaign_id;
    const gaCookie = useCookie("_ga");
    const gclid = router.query.gclid;
    const subid = router.query.subid;
    const timeCookie = 1000 * 60 * 60 * 24 * 7;
    const fbc = useCookie("_fbc");
    const fbp = useCookie("_fbp");
    const payload = {
      ...router.query,
      ...(fbc && { fbc: fbc.value }),
      ...(fbp && { fbp: fbp.value })
    };

    if (utmSource) {
      useCookie("utm_source", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = utmSource;
    }

    if (src) {
      useCookie("src", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = src;
    }

    if (utmCampaign) {
      useCookie("utm_campaign", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = utmCampaign;
    }

    if (utmContent) {
      useCookie("utm_content", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = utmContent;
    }

    if (fbcParam) {
      useCookie("_fbc", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = fbcParam;
    }

    if (fbpParam) {
      useCookie("_fbp", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = fbpParam;
    }

    if (btag) {
      useCookie("btag", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = btag;
    }

    if (refererId) {
      useCookie("referrer_id", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = refererId;
    }

    if (code) {
      useCookie("code", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = code;
    }

    if (clickid) {
      useCookie("clickid", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = clickid;
    }

    if (utmMedium) {
      useCookie("utm_medium", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = utmMedium;
    }

    if (utmTerm) {
      useCookie("utm_term", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = utmTerm;
    }

    if (placement) {
      useCookie("placement", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = placement;
    }

    if (afp) {
      useCookie("afp", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = afp;
    }

    if (afp1) {
      useCookie("afp1", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = afp1;
    }

    if (afp2) {
      useCookie("afp2", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = afp2;
    }

    if (afp3) {
      useCookie("afp3", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = afp3;
    }

    if (afp4) {
      useCookie("afp4", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = afp4;
    }

    if (afp5) {
      useCookie("afp5", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = afp5;
    }

    if (afp6) {
      useCookie("afp6", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = afp6;
    }

    if (afp7) {
      useCookie("afp7", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = afp7;
    }

    if (afp8) {
      useCookie("afp8", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = afp8;
    }

    if (afp9) {
      useCookie("afp9", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = afp9;
    }

    if (shareCode) {
      useCookie("shareCode", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = shareCode;
    }

    if (source_id) {
      useCookie("source_id", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = source_id;
    }

    if (event_id) {
      useCookie("event_id", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = event_id;
    }

    if (campaign_id) {
      useCookie("campaign_id", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = campaign_id;
    }

    if (gaCookie?.value) {
      const parts = gaCookie.value.split(".");
      const clientId = parts.slice(2).join(".");
      useCookie("client_id", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = clientId;

      payload.client_id = clientId;
    }

    if (gclid) {
      useCookie("gclid", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = gclid;
    }

    if (subid) {
      useCookie("subid", {
        sameSite: "none",
        secure: true,
        maxAge: timeCookie
      }).value = subid;
    }

    if (
      !authStore.isLoggedIn &&
      !isEmpty(router.query) &&
      !router.query?.token &&
      !router.query.login &&
      !router.query.showCookieModal
    ) {
      authStore.modalOpen("register");
    }

    if (!authStore.isLoggedIn && code) {
      if (login === "1") {
        authStore.modalOpen("login");
        return;
      } else if (!login === "0") {
        authStore.modalOpen("register");
        return;
      }
    }

    // Removing coupon code if user reloads the page
    if (!code && useCookie("code").value) {
      useCookie("code").value = "";
    }

    useCookie("params", {
      sameSite: "none",
      secure: true,
      maxAge: timeCookie
    }).value = JSON.stringify(payload);
  }
});

watch(loading, async () => {
  let code = useCookie("code").value;
  // Applying coupon if user is logged in
  if (authStore.isLoggedIn && code) {
    try {
      await couponStore.applyCouponCode(code);
      depositStore.toggleModal();

      showAlert("success", "Cupom ativado com sucesso");
    } catch (error) {
      console.log(error);

      showAlert("error", error);
    }
  }
});


const showVPNModal = computed(() => legitimuzStore.isVPNDetected);

watch(() => route.path, (newPath, oldPath) => {
  if (newPath !== oldPath && authStore.isLoggedIn) {

    legitimuzStore.checkStatus(false);
  }
}, { immediate: false });

// SEO Meta tags (keep these for social media/SEO)
useSeoMeta({
  description: computed(() => seoStore.seo?.content ?? "Apostas online"),
  ogImage: computed(() => settingsStore.settings.general_site_favicon ?? ""),
  ogSiteName: computed(() => settingsStore.settings.general_site_title ?? ""),
  ogType: "website",
  ogDescription: computed(() => seoStore.seo?.content ?? "Apostas online"),
  robots: "index, follow",
  ogImageUrl: computed(() => settingsStore.settings.general_site_favicon ?? ""),
});

/**
 * @todo Refactor the SEO system to use a single composable
 * @todo Remove the seo.ts plugin and put the in onbeforeRouteUpdate() hook
 * @todo Test SEO to the home page
 * @todo Test dynamic SEO to the home page
 * @todo Test dynamic SEO to the casino page
 * @todo Test dynamic SEO to the sports page
 */

// Fetch SEO data on initial load
await seoStore.fetchPageSeo();

// Centralized computed title
const defaultTitle = computed(() => settingsStore.settings.general_site_title ?? 'Apostas Online');
const pageTitle = computed(() =>
  seoStore.seo.title
    ? `${seoStore.seo.title} | ${defaultTitle.value}`
    : `Apostas Online | ${defaultTitle.value}`
);

// SEO Meta tags
useHead({
  title: pageTitle,
  link: [
    {
      rel: "icon",
      type: "image/x-icon",
      href: () => settingsStore.settings.general_site_favicon
    },
    {
      rel: "canonical",
      href: () => url.origin
    }
  ]
});

// SEO Meta tags on route change
watch(
  () => route.fullPath,
  async () => {
    await seoStore.fetchPageSeo();
    console.log('route.fullPath', route.fullPath);
  }
);
</script>

<template>
  <div>
    <Outdated />
    <NuxtLayout :name="layoutName">
      <NuxtPage />
    </NuxtLayout>
    <CookiesAccessModal />
    <VpnModal v-model="showVPNModal" />

    <client-only>
      <LiveChatWidget
        v-if="settingsStore.settings.general_show_chat_widget && liveChatConfig.license !== undefined && route.name !== 'play-id'"
        :license="liveChatConfig.license" :group="liveChatConfig.group ? liveChatConfig.group : ''"
        :visibility="menu.chatOpened ? 'maximized' : 'minimized'" />

      <!-- TODO: Refactor this to a component -->
      <div v-show="isUserOffline" class="absolute bottom-10 w-full z-[9999] p-8 flex justify-center items-center">
        <div
          class="bg-secondary text-white max-w-96 p-6 rounded-lg text-center items-center flex flex-col leading-none shadow-lg">
          <Icon name="fa6-solid:triangle-exclamation" class="w-12 h-12 mb-4 text-warning" />
          <h3 class="text-lg leading-tight mb-2 font-semibold">Parece que você perdeu sua <br />conexão com a internet!
          </h3>
          <small class="opacity-70">Conecte-se para continuar navegando.</small>
        </div>
      </div>
    </client-only>

  </div>
</template>

<style>
.page-enter-active,
.page-leave-active {
  transition: all 0.4s;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
  transform: translateY(10px);
  /* adjust as needed */
}

.page-enter-to,
.page-leave-from {
  transform: translateY(0);
}

.styledOverflow {
  ::-webkit-scrollbar {
    height: 0.5em;
  }

  ::-webkit-scrollbar-track {
    box-shadow: inset 0 0 1px grey;
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb {
    background: rgb(53, 51, 51);
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: rgb(53, 51, 51);
  }
}

#spinner #sGD stop {
  stop-color: var(--bz-primary);
}

#spinner .path-solid {
  stroke: var(--bz-primary);
}
</style>
